import { Link } from "react-router-dom";
import { Phone, MessageSquareText, Mail, Facebook } from "lucide-react";
import Seo, { breadcrumbs, contactPageSchema, personSchema, webPageSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import CtaCallout from "../components/CtaCallout";
import { SectionHeader } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
// ReviewCta is now gated to lead-submit success.
import { siteConfig } from "../config";

export default function Contact() {
  return (
    <main id="main-content">
      <Seo
        title="Contact Scott Hall | Scott Sells Chevys"
        description="Contact Scott Hall directly — text, call, or email — for help with new and used vehicles, trade value, payment direction, EV questions, and vehicle availability in Central Ohio."
        canonicalPath="/contact"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
          contactPageSchema({ path: "/contact" }),
          personSchema(),
          webPageSchema({ name: "Contact Scott Hall", path: "/contact", description: "Reach Scott Hall by text, call, or email." }),
        ]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">Contact Scott</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">Have a question about a vehicle, trade, payment, availability, or EV options? Send me a message and I'll help with the next step.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={siteConfig.scott.phoneTel} className="ssc-btn ssc-btn-navy"><Phone size={16} /> Call Scott</a>
            <a href={siteConfig.scott.phoneSms} className="ssc-btn ssc-btn-primary"><MessageSquareText size={16} /> Text Scott</a>
            <a href={siteConfig.scott.emailMailto} className="ssc-btn ssc-btn-outline"><Mail size={16} /> Email Scott</a>
            <a href={siteConfig.links.facebook} target="_blank" rel="noopener noreferrer" className="ssc-btn ssc-btn-outline"><Facebook size={16} /> Facebook</a>
            <Link to="/find-me-a-vehicle" className="ssc-btn ssc-btn-red">Find Me a Vehicle</Link>
          </div>
        </div>
      </section>
      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <AboutScottBlurb
            heading="Talk Directly With Scott"
            text="With over 15 years selling cars, Scott helps Central Ohio drivers compare new and used options, ask better questions, understand trade value, explore payment direction, and feel more comfortable with the next step."
            ctaLabel="Meet Scott"
            testId="contact-about-scott"
            className="mb-8"
          />
          {/* ReviewCta removed — now shown only on lead-submit success */}
        </div>
      </section>
      <section className="ssc-section">
        <div className="ssc-container max-w-2xl">
          <SectionHeader title="Send a message" />
          <LeadForm
            leadType="contact"
            leadSource="Contact Page"
            testIdPrefix="contact-lead"
            fields={[
              { name: "name", label: "Name", required: true, autoComplete: "name" },
              { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
              { name: "email", label: "Email", type: "email", autoComplete: "email" },
              { name: "topic", label: "What can I help with?", type: "select", options: [
                "I'm interested in a specific vehicle",
                "I need help finding a vehicle",
                "I want to build my own deal",
                "I have a trade",
                "I have a payment question",
                "I want EV help",
                "I want accessories",
                "Other",
              ]},
              { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
              { name: "best_time", label: "Best Time to Contact" },
              { name: "message", label: "Message", type: "textarea", full: true },
            ]}
            submitLabel="Send Message"
          />
          <CtaCallout
            testId="contact-cta-callout"
            heading="Don't feel like filling out the form?"
            sub="Text or call Scott directly — fastest way to get a real answer."
            actions={[
              { key: "text", label: "Text Scott", icon: "text", href: siteConfig.scott.phoneSms },
              { key: "call", label: "Call Scott", icon: "call", href: siteConfig.scott.phoneTel },
              { key: "find", label: "Find Me a Vehicle", icon: "find", to: "/find-me-a-vehicle" },
              { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
            ]}
          />
        </div>
      </section>
    </main>
  );
}
